// Page Me — the app's state. See docs/BUILD-CONTRACT.md "Client store API".
//
// Persistence: pages live in IndexedDB (`records`, id `page:<id>`); everything
// else is small and lives in `prefs` (localStorage) under `pm.*`. Hydrated at
// module load. At /demo everything is seeded in memory, nothing is persisted and
// the server is never called.
//
// The rules themselves (merging, receipts, the cap, conversations) are pure
// functions in src/pm/model.js so they can be tested without a browser.
//
// Never log the token, the activation code or a page body.

import { ref, reactive, computed, watch } from 'vue';
import { records, prefs } from './core/storage.js';
import { bus, state } from './core/state.js';
import { isDemo, demoData } from './core/demo.js';
import { announce } from './core/a11y.js';
import { remove as removeWithUndo } from './core/undo.js';
import { haptic, debounce } from './core/util.js';
import { isNative, plugin, shareOrigin } from './core/native.js';
import { pushAvailable } from '../addons/push/index.js';

import { api, setAuthToken, setOnUnauthorized, isTransient } from './pm/api.js';
import { alertFor, setAlertSource } from './pm/beep.js';
import { requestBeeps, setPushOwner, resetPushReport } from './pm/push.js';
import { DEVICES, DEFAULT_DEVICE, deviceById } from './pm/devices.js';
import {
  OPERATOR_NUMBER, formatNumber, formatIso, parseNumberInput, STATUS_MARK, secondaryLine, deliveryMark,
} from './pm/format.js';
import { validateBody, normalizeBody } from './pm/text.js';
import { codeFor } from './pm/codebook.js';
import {
  INBOX_SIZE, operatorFriend, displayName, mergeFriends, upsertFriend, mergeIncoming,
  applyReceipts, sortPages, capPages, buildConversations, sortAccepted, computeStats,
  exportLines, moveItem, newClientId, backoffDelay, mergeGroups, groupTimeline, eventLine,
} from './pm/model.js';

// ── constants ────────────────────────────────────────────────────────────────

const K = {
  me: 'pm.me',
  friends: 'pm.friends',
  settings: 'pm.settings',
  presets: 'pm.presets',
  drafts: 'pm.drafts',
  flags: 'pm.flags',
  counters: 'pm.counters',
  prefsPending: 'pm.prefsPending',
  groups: 'pm.groups',
};
const PAGE_PREFIX = 'page:';
const POLL_MS = 15_000;
const WELCOME_DELAY_MS = 2_000;
const TEXT_SIZES = [1, 1.25, 1.5, 2];
const ALERT_MODES = ['beep', 'vibrate', 'silent'];
const HANDLE_RE = /^[A-Z0-9 .'_-]{1,12}$/;
const GROUP_NAME_RE = /^[A-Z0-9 .'&!_-]{1,16}$/;
export const MAX_GROUP = 12;
const DEFAULT_PRESETS = ['ON MY WAY', 'CALL ME', 'RUNNING LATE', 'OK', '😂', '❤️'];

const emptyMe = () => ({
  number: null, handle: null, inviteCode: null, activationCode: null, activationCodeShown: false, token: null,
});
const defaultSettings = () => ({
  device: DEFAULT_DEVICE, alertMode: 'beep', highContrast: false, textSize: 1, largeControls: false,
});
const defaultFlags = () => ({
  notifAsked: 0,     // 0 never asked · 1 "Not now" · 2 answered (granted/denied)
  sentOnce: false,   // a page has left this pager
  reoffer: false,    // a real friend paged after "Not now"
  reoffered: false,  // …and the re-offer was shown and dismissed
  nameAsked: false,  // "What should friends see?" was answered or skipped
});
const defaultCounters = () => ({ sent: 0, received: 0, codes: {} });
let presetSeq = 0;
const presetId = () => `p${Date.now().toString(36)}${(presetSeq += 1).toString(36)}`;
const defaultPresets = () => DEFAULT_PRESETS.map((text) => ({ id: presetId(), text }));

const live = () => !isDemo.value;
const ok = (extra = {}) => ({ ok: true, ...extra });
const err = (error, message, extra = {}) => ({ ok: false, error, message, ...extra });
const DEMO_ERR = () => err('demo', "That doesn't work in the demo. Install Page Me to get your own number.");

// ── state ────────────────────────────────────────────────────────────────────

export const me = ref(emptyMe());
export const activation = ref('idle');
export const friends = ref([]);
export const pages = ref([]);
export const settings = reactive(defaultSettings());
export const presets = ref([]);
export const drafts = reactive({});
export const lastArrival = ref(null);
/** Groups you are in: [{ id, name, owner, members: [{ number, handle }], events, createdAt, updatedAt, lastPageAt }]. */
export const groups = ref([]);

const flags = reactive(defaultFlags());
const counters = reactive(defaultCounters());

// ── computed ─────────────────────────────────────────────────────────────────

const blockedSet = computed(() => new Set(friends.value.filter((f) => f.status === 'blocked').map((f) => f.number)));

export const acceptedFriends = computed(() => sortAccepted(friends.value));
export const pendingIn = computed(() => friends.value.filter((f) => f.status === 'pending-in'));
export const pendingOut = computed(() => friends.value.filter((f) => f.status === 'pending-out'));
export const blockedFriends = computed(() => friends.value.filter((f) => f.status === 'blocked'));

export const inbox = computed(() => pages.value
  .filter((p) => p.dir === 'in' && !blockedSet.value.has(p.peer))
  .slice(0, INBOX_SIZE));

export const conversations = computed(() => buildConversations(pages.value, friends.value, groups.value));

export const unreadCount = computed(() => pages.value
  .filter((p) => p.dir === 'in' && !p.read && !blockedSet.value.has(p.peer)).length);

export const currentDevice = computed(() => deviceById(settings.device));

export const stats = computed(() => {
  if (isDemo.value) {
    const s = computeStats(pages.value);
    return { sent: s.sent, received: s.received, topCode: s.topCode };
  }
  let topCode = null;
  for (const [code, n] of Object.entries(counters.codes)) {
    if (!topCode || n > counters.codes[topCode]) topCode = code;
  }
  return { sent: counters.sent, received: counters.received, topCode };
});

export const inviteLink = computed(() => (me.value.inviteCode
  ? `${shareOrigin()}/add/${me.value.inviteCode}`
  : ''));

export const activationCardDue = computed(() => !!me.value.activationCode
  && !me.value.activationCodeShown
  && friends.value.some((f) => f.status === 'accepted' && !f.system));

/** Ask "What should friends see?" — a pager with a number and no name, not yet asked. */
export const nameOffer = computed(() => !!me.value.number && !me.value.handle && !flags.nameAsked
  && activation.value === 'ready');

export const beepsOffer = computed(() => {
  if (!pushAvailable()) return false;         // web: nothing to turn on
  if (flags.notifAsked === 0) return flags.sentOnce;
  if (flags.notifAsked === 1) return flags.reoffer && !flags.reoffered;
  return false;
});

// ── persistence ──────────────────────────────────────────────────────────────

function savePages(list) {
  if (!live()) return;
  for (const p of list) records.put({ id: PAGE_PREFIX + p.id, data: { ...p } }).catch(() => {});
}
function forgetPages(list) {
  if (!live()) return;
  for (const p of list) records.delete(PAGE_PREFIX + p.id).catch(() => {});
}

/** Replace the page list (newest first), enforce the cap, persist `changed`. */
function commitPages(next, changed = []) {
  const { pages: kept, dropped } = capPages(sortPages(next));
  pages.value = kept;
  savePages(changed.filter((p) => !dropped.some((d) => d.id === p.id)));
  forgetPages(dropped);
}

function patchPage(id, patch) {
  let updated = null;
  const next = pages.value.map((p) => {
    if (p.id !== id) return p;
    updated = { ...p, ...patch };
    return updated;
  });
  if (!updated) return null;
  pages.value = next;
  savePages([updated]);
  return updated;
}

const savePrefsNow = () => {
  if (!live()) return;
  if (me.value.number) prefs[K.me] = { ...me.value };
  prefs[K.friends] = friends.value;
  prefs[K.groups] = groups.value;
  prefs[K.settings] = { ...settings };
  prefs[K.presets] = presets.value;
  prefs[K.flags] = { ...flags };
  prefs[K.counters] = { ...counters, codes: { ...counters.codes } };
};
const saveDrafts = debounce(() => { if (live()) prefs[K.drafts] = { ...drafts }; }, 300);

// ── display attributes on <html> ─────────────────────────────────────────────

function applyDisplay() {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  el.dataset.lcdScale = String(settings.textSize);
  if (settings.highContrast) el.dataset.lcdContrast = 'high';
  else delete el.dataset.lcdContrast;
  if (settings.largeControls) el.dataset.largeControls = 'true';
  else delete el.dataset.largeControls;
}

// ── hydrate ──────────────────────────────────────────────────────────────────

let resolveReady;
const ready = new Promise((r) => { resolveReady = r; });

function hydrate() {
  if (isDemo.value) {
    seedDemo();
    resolveReady();
    return;
  }
  me.value = { ...emptyMe(), ...(prefs[K.me] ?? {}) };
  friends.value = Array.isArray(prefs[K.friends]) ? prefs[K.friends] : [];
  groups.value = Array.isArray(prefs[K.groups]) ? prefs[K.groups] : [];
  Object.assign(settings, defaultSettings(), prefs[K.settings] ?? {});
  if (!DEVICES.some((d) => d.id === settings.device)) settings.device = DEFAULT_DEVICE;
  presets.value = Array.isArray(prefs[K.presets]) ? prefs[K.presets] : defaultPresets();
  Object.assign(drafts, prefs[K.drafts] ?? {});
  Object.assign(flags, defaultFlags(), prefs[K.flags] ?? {});
  const hadCounters = !!prefs[K.counters];
  Object.assign(counters, defaultCounters(), prefs[K.counters] ?? {});

  setAuthToken(me.value.token);
  if (me.value.token && me.value.number) {
    activation.value = 'ready';
    setPushOwner(me.value.number);
  }

  let loading;
  try { loading = records.all(); } catch (e) { loading = Promise.reject(e); }
  Promise.resolve(loading)
    .then((rows) => {
      const stored = (rows ?? [])
        .filter((r) => typeof r?.id === 'string' && r.id.startsWith(PAGE_PREFIX) && r.data)
        .map((r) => r.data);
      const seen = new Set(pages.value.map((p) => p.id));
      pages.value = sortPages([...pages.value, ...stored.filter((p) => !seen.has(p.id))]);
      if (!hadCounters && pages.value.length) {
        const s = computeStats(pages.value);
        Object.assign(counters, { sent: s.sent, received: s.received, codes: s.codes });
      }
    })
    .catch(() => { /* no IndexedDB (private mode, tests): history is session-only */ })
    .finally(() => {
      resolveReady();
      // Look at once on launch: names, groups and pages that changed while the
      // app was closed shouldn't wait for the first 15s poll.
      if (activation.value === 'ready') { startNetwork(); refreshInbox(); }
    });
}

// ── arrivals ─────────────────────────────────────────────────────────────────

function countCode(body) {
  const c = codeFor(body);
  if (c) counters.codes[c.code] = (counters.codes[c.code] ?? 0) + 1;
}

function touchFriend(number, at, handle) {
  let changed = false;
  const next = friends.value.map((f) => {
    if (f.number !== number) return f;
    const upd = { ...f };
    if (at && at > (f.lastPageAt ?? 0)) { upd.lastPageAt = at; changed = true; }
    if (handle && handle !== f.handle) { upd.handle = handle; changed = true; }
    return upd;
  });
  if (changed) friends.value = next;
}

/** New pages landed (oldest first): flash, beep, announce, count. */
function arrived(added) {
  if (!added.length) return;
  for (const p of added) {
    counters.received += 1;
    countCode(p.body);
    touchFriend(p.peer, p.sentAt);
  }
  const newest = added[added.length - 1];
  lastArrival.value = newest;
  try { alertFor(newest); } catch { /* audio is best-effort */ }
  const name = pageFrom(newest);
  announce(added.length === 1
    ? `Page from ${name}: ${newest.body}`
    : `${added.length} new pages. Latest from ${name}: ${newest.body}`);
  if (flags.notifAsked === 1 && added.some((p) => p.peer !== OPERATOR_NUMBER)) flags.reoffer = true;
}

// ── network: inbox ───────────────────────────────────────────────────────────

let inboxInFlight = null;

export function refreshInbox() {
  if (isDemo.value) return Promise.resolve(ok());
  if (inboxInFlight) return inboxInFlight;
  inboxInFlight = (async () => {
    await ready;
    if (!me.value.token) return err('not_activated', "This pager doesn't have a number yet.");
    const r = await api.get('/api/inbox');
    if (!r.ok) return r;

    const at = Date.now();
    if (Array.isArray(r.friends)) friends.value = mergeFriends(friends.value, r.friends, at);
    if (Array.isArray(r.groups)) groups.value = mergeGroups(groups.value, r.groups);

    const handles = new Map((r.pages ?? []).map((p) => [p.from, p.fromHandle]));
    for (const [n, h] of handles) if (h) touchFriend(n, 0, h);

    const merged = mergeIncoming(pages.value, r.pages ?? [], at);
    const rec = applyReceipts(merged.pages, r.receipts ?? []);
    commitPages(rec.pages, [...merged.added, ...rec.changed]);
    arrived(merged.added);

    const ids = (r.pages ?? []).map((p) => String(p.id)).filter(Boolean);
    if (ids.length) await api.post('/api/inbox/ack', { ids });

    flushPrefs();
    if (!retryTimer && pages.value.some((p) => p.status === 'queued')) {
      attempt = 0;
      retryOutbox();
    }
    return ok({ added: merged.added.length });
  })().finally(() => { inboxInFlight = null; });
  return inboxInFlight;
}

// ── network: outbox ──────────────────────────────────────────────────────────

let attempt = 0;
let retryTimer = 0;
let outboxInFlight = null;

function scheduleRetry() {
  clearTimeout(retryTimer);
  retryTimer = 0;
  const delay = backoffDelay(attempt);
  attempt += 1;
  if (delay == null) return;          // wait for online / resume / a good poll
  retryTimer = setTimeout(() => { retryTimer = 0; retryOutbox(); }, delay);
}

/** POST one page. → the API result; updates the page's status. */
async function postPage(page) {
  const r = page.groupId
    ? await api.post(`/api/groups/${encodeURIComponent(page.groupId)}/pages`, { body: page.body, clientId: page.clientId })
    : await api.post('/api/pages', { to: page.peer, body: page.body, clientId: page.clientId });
  const current = pages.value.find((p) => p.id === page.id);
  if (!current) return r;             // deleted while sending
  if (r.ok) {
    const extra = page.groupId && Number.isFinite(r.recipients) ? { recipients: r.recipients } : {};
    if (current.status === 'queued') patchPage(page.id, { status: 'network', serverId: r.id ?? null, ...extra });
  } else if (!isTransient(r) && r.error !== 'unauthorized') {
    patchPage(page.id, { status: 'failed', failReason: r.message });
  }
  return r;
}

export function retryOutbox() {
  if (isDemo.value) return Promise.resolve(ok({ sent: 0 }));
  if (outboxInFlight) return outboxInFlight;
  outboxInFlight = (async () => {
    await ready;
    if (!me.value.token) return err('not_activated', "This pager doesn't have a number yet.");
    const queue = pages.value.filter((p) => p.dir === 'out' && p.status === 'queued')
      .sort((a, b) => a.sentAt - b.sentAt);
    let sent = 0;
    for (const p of queue) {
      const r = await postPage(p);
      if (r.ok) { sent += 1; continue; }
      if (isTransient(r) || r.error === 'unauthorized') {
        scheduleRetry();
        return err(r.error, r.message, { sent, remaining: queue.length - sent });
      }
    }
    attempt = 0;
    clearTimeout(retryTimer);
    retryTimer = 0;
    return ok({ sent });
  })().finally(() => { outboxInFlight = null; });
  return outboxInFlight;
}

// ── network: prefs mirror ────────────────────────────────────────────────────

function queuePrefs(patch) {
  if (!live()) return;
  prefs[K.prefsPending] = { ...(prefs[K.prefsPending] ?? {}), ...patch };
  flushPrefs();
}

let prefsInFlight = false;
async function flushPrefs() {
  if (!live() || prefsInFlight || !me.value.token) return;
  const patch = prefs[K.prefsPending];
  if (!patch || !Object.keys(patch).length) return;
  prefsInFlight = true;
  let again = false;
  try {
    const r = await api.put('/api/pager/prefs', patch);
    if (r.ok || !isTransient(r)) {
      const now = prefs[K.prefsPending] ?? {};
      for (const k of Object.keys(patch)) if (now[k] === patch[k]) delete now[k];
      prefs[K.prefsPending] = now;
      again = Object.keys(now).length > 0;   // something changed while we were sending
    }
  } finally { prefsInFlight = false; }
  if (again) flushPrefs();
}

// ── polling & wake-ups ───────────────────────────────────────────────────────

let networkStarted = false;
let pollTimer = 0;
const visible = () => typeof document === 'undefined' || document.visibilityState === 'visible';

function wake() {
  if (!live()) return;
  if (activation.value === 'offline' && !me.value.token) { activate(); return; }
  if (activation.value !== 'ready') return;
  attempt = 0;
  refreshInbox();
  retryOutbox();
}

function startNetwork() {
  if (networkStarted || !live()) return;
  networkStarted = true;
  pollTimer = setInterval(() => {
    if (visible() && activation.value === 'ready') refreshInbox();
  }, POLL_MS);
}

function stopNetwork() {
  clearInterval(pollTimer);
  clearTimeout(retryTimer);
  pollTimer = 0;
  retryTimer = 0;
  networkStarted = false;
}

if (typeof document !== 'undefined' && document.addEventListener) {
  document.addEventListener('visibilitychange', () => { if (visible()) wake(); });
}
if (isNative) {
  try { plugin('App')?.addListener?.('resume', wake); } catch { /* ignore */ }
}
let wasOnline = state.online;
bus.on('state', ({ changed }) => {
  if (!changed?.includes('online')) return;
  const nowOnline = state.online;
  if (nowOnline && !wasOnline) {
    if (activation.value === 'offline') activate();
    else wake();
  }
  wasOnline = nowOnline;
});
bus.on('push:received', () => { if (live()) refreshInbox(); });
bus.on('push:opened', () => { if (live()) refreshInbox(); });

// ── actions: activation ──────────────────────────────────────────────────────

let activationInFlight = null;
let activationRetryTimer = 0;
let activationAttempt = 0;
// No number yet because the network is unreachable: keep trying on our own.
// The browser's `online` event never fires when it was the server, not the
// Wi-Fi, that was down — or on a phone that never noticed it was offline.
const ACTIVATION_RETRY_MS = [5_000, 15_000, 60_000];

export function activate() {
  if (isDemo.value) {
    activation.value = 'ready';
    return Promise.resolve(ok());
  }
  if (activationInFlight) return activationInFlight;
  activationInFlight = (async () => {
    await ready;
    if (me.value.token && me.value.number) {
      activation.value = 'ready';
      startNetwork();
      return ok();
    }
    activation.value = 'activating';
    const r = await api.post('/api/pager', {}, { auth: false });
    if (!r.ok) {
      activation.value = r.network ? 'offline' : 'error';
      if (r.network || isTransient(r)) {
        clearTimeout(activationRetryTimer);
        const delay = ACTIVATION_RETRY_MS[Math.min(activationAttempt, ACTIVATION_RETRY_MS.length - 1)];
        activationAttempt += 1;
        activationRetryTimer = setTimeout(() => {
          activationRetryTimer = 0;
          if (!me.value.token && activation.value !== 'activating') activate();
        }, delay);
      }
      return r;
    }
    clearTimeout(activationRetryTimer);
    activationRetryTimer = 0;
    activationAttempt = 0;
    me.value = {
      number: r.number,
      token: r.token,
      handle: r.handle ?? null,
      inviteCode: r.inviteCode ?? null,
      activationCode: r.activationCode ?? null,
      activationCodeShown: false,
    };
    setAuthToken(r.token);
    friends.value = mergeFriends([], [{ number: OPERATOR_NUMBER, handle: 'OPERATOR', status: 'accepted', system: true }]);
    groups.value = [];
    savePrefsNow();
    activation.value = 'ready';
    setPushOwner(r.number);
    startNetwork();
    queuePrefs({ device: settings.device, alertMode: settings.alertMode });
    // OPERATOR's welcome is already waiting on the server; collect it a beat
    // later so it lands as an arrival rather than as part of activation.
    setTimeout(() => { refreshInbox(); }, WELCOME_DELAY_MS);
    bus.emit('pm:activated', r.number);
    return ok();
  })().finally(() => { activationInFlight = null; });
  return activationInFlight;
}

export async function restore(code) {
  if (isDemo.value) return DEMO_ERR();
  const typed = String(code ?? '').trim();
  if (!typed) return err('bad_code', 'Activation codes look like K7QX-9MFD-2HTP.');
  const r = await api.post('/api/pager/restore', { code: typed }, { auth: false });
  if (!r.ok) return r;
  await ready;
  const canonical = typed.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
  forgetPages(pages.value);
  pages.value = [];
  for (const k of Object.keys(drafts)) delete drafts[k];
  me.value = {
    number: r.number,
    token: r.token,
    handle: r.handle ?? null,
    inviteCode: r.inviteCode ?? null,
    activationCode: canonical.length === 12
      ? `${canonical.slice(0, 4)}-${canonical.slice(4, 8)}-${canonical.slice(8)}`
      : typed,
    activationCodeShown: true,
  };
  setAuthToken(r.token);
  friends.value = mergeFriends([], r.friends ?? []);
  groups.value = [];
  savePrefsNow();
  resetPushReport();
  setPushOwner(r.number);
  activation.value = 'ready';
  startNetwork();
  queuePrefs({ device: settings.device, alertMode: settings.alertMode });
  refreshInbox();
  announce(`Restored ${formatNumber(r.number)}.`);
  return ok({ number: r.number });
}

let disconnecting = false;

/**
 * The server refused this pager's token: its activation code was used on
 * another phone (which now owns the number and its friends), or it was
 * deleted. Stop using the dead token, keep this phone's page history, fail
 * anything still waiting to go, and get this phone a fresh number — Settings →
 * Restore can still bring the old one back.
 */
function numberMovedAway(refusedToken) {
  if (!live() || disconnecting || !me.value.token || refusedToken !== me.value.token) return;
  stopNetwork();
  setAuthToken(null);
  setPushOwner(null);
  resetPushReport();
  delete prefs[K.prefsPending];
  delete prefs[K.me];
  const moved = pages.value.filter((p) => p.dir === 'out' && p.status === 'queued')
    .map((p) => ({ ...p, status: 'failed', failReason: 'This number moved to another phone.' }));
  if (moved.length) {
    const ids = new Set(moved.map((p) => p.id));
    commitPages([...moved, ...pages.value.filter((p) => !ids.has(p.id))], moved);
  }
  const old = me.value.number;
  me.value = emptyMe();
  friends.value = [];
  groups.value = [];
  activation.value = 'idle';
  announce(`${formatNumber(old ?? '')} is now on another phone. Getting this pager a new number.`);
  bus.emit('pm:number-moved', old);
  activate();
}
setOnUnauthorized(numberMovedAway);

export async function disconnect() {
  if (isDemo.value) return DEMO_ERR();
  if (me.value.token) {
    disconnecting = true;
    const r = await api.del('/api/pager').finally(() => { disconnecting = false; });
    if (!r.ok && r.httpStatus !== 401) return r;
  }
  stopNetwork();
  forgetPages(pages.value);
  try {
    const rows = await records.all();
    for (const row of rows ?? []) {
      if (String(row?.id ?? '').startsWith(PAGE_PREFIX)) await records.delete(row.id);
    }
  } catch { /* nothing stored */ }
  for (const k of Object.values(K)) delete prefs[k];
  resetPushReport();
  setAuthToken(null);
  setPushOwner(null);
  me.value = emptyMe();
  friends.value = [];
  groups.value = [];
  pages.value = [];
  for (const k of Object.keys(drafts)) delete drafts[k];
  Object.assign(flags, defaultFlags());
  Object.assign(counters, defaultCounters());
  presets.value = defaultPresets();
  lastArrival.value = null;
  activation.value = 'idle';
  // "…then returns to activation" (IDEATION, Settings → Danger). The Pager
  // screen only activates when it mounts, and it is already mounted.
  activate();
  return ok();
}

// ── actions: pages ───────────────────────────────────────────────────────────

export async function sendPage(to, body) {
  const number = parseNumberInput(to);
  if (!number) return err('bad_number', 'Pick who to page.');
  const problem = validateBody(body);
  if (problem) return err('bad_body', problem);
  const f = friendByNumber(number);
  if (live() && f && f.status !== 'accepted') {
    return err('not_friends', f.status === 'pending-out'
      ? 'Waiting for them to accept.'
      : "Couldn't deliver — you're not connected to this pager.");
  }
  await ready;
  const now = Date.now();
  const clientId = newClientId();
  const page = {
    id: clientId, clientId, dir: 'out', peer: number, body: normalizeBody(body),
    sentAt: now, receivedAt: now, status: 'queued', read: true, locked: false,
  };
  commitPages([page, ...pages.value], [page]);
  counters.sent += 1;
  countCode(page.body);
  touchFriend(number, now);
  delete drafts[number];
  flags.sentOnce = true;
  haptic('tick');

  if (isDemo.value) {
    demoDeliver(page);
    return ok({ page });
  }
  if (!me.value.token) {
    // Not activated yet (offline first launch): it waits in the outbox.
    return ok({ page, queued: true });
  }
  const r = await postPage(page);
  const current = pages.value.find((p) => p.id === page.id) ?? page;
  if (r.ok) {
    // A reply (OPERATOR's in 2–4s, a friend's delivery receipt) usually lands
    // well inside the 15s poll. Look sooner, a few times, so it feels live.
    for (const ms of [3000, 6000, 10000]) setTimeout(() => { refreshInbox(); }, ms);
    return ok({ page: current });
  }
  if (current.status === 'queued') {
    scheduleRetry();
    return ok({ page: current, queued: true });
  }
  return err(r.error, r.message, { page: current });
}

export function markRead(id) {
  const p = pages.value.find((x) => x.id === id);
  if (!p || p.read) return;
  patchPage(id, { read: true });
}

export function toggleLock(id) {
  const p = pages.value.find((x) => x.id === id);
  if (!p) return;
  const updated = patchPage(id, { locked: !p.locked });
  announce(updated.locked ? 'Page locked.' : 'Page unlocked.');
}

export function deletePage(id) {
  const at = pages.value.findIndex((x) => x.id === id);
  if (at < 0) return err('not_found', 'That page is already gone.');
  const page = pages.value[at];
  pages.value = pages.value.filter((x) => x.id !== id);
  if (lastArrival.value?.id === id) lastArrival.value = null;
  removeWithUndo({
    text: 'Page deleted.',
    ms: 5000,
    commit: () => forgetPages([page]),
    revert: () => {
      if (!pages.value.some((x) => x.id === id)) pages.value = sortPages([page, ...pages.value]);
    },
  });
  return ok();
}

export function deleteAllPages() {
  const all = pages.value;
  pages.value = [];
  lastArrival.value = null;
  forgetPages(all);
  announce('All pages deleted from this phone.');
  return ok({ deleted: all.length });
}

/** 1:1 pages with one friend, oldest first (their group pages live in the group). */
export function threadWith(number) {
  return pages.value.filter((p) => p.peer === String(number) && !p.groupId).sort((a, b) => a.sentAt - b.sentAt);
}

// ── actions: friends ─────────────────────────────────────────────────────────

export function friendByNumber(number) {
  return friends.value.find((f) => f.number === String(number ?? ''));
}

/** A group-mate who isn't your friend: `{ number, handle }` from a group you share. */
function memberRecord(n) {
  for (const g of groups.value) {
    const m = g.members?.find((x) => x.number === n);
    if (m) return m;
  }
  return undefined;
}

/** THE display rule (src/pm/format.js): your name for them ▸ their handle ▸ their number. */
export function friendName(number) {
  const n = String(number ?? '');
  if (n && n === me.value.number) return me.value.handle || 'YOU';
  return displayName(friendByNumber(n) ?? memberRecord(n), n);
}

/** The line under a name in a list: their number (and their handle when you renamed them). */
export function friendSecondary(number) {
  const n = String(number ?? '');
  if (n && n === me.value.number) return me.value.handle ? formatNumber(n) : '';
  return secondaryLine(friendByNumber(n) ?? memberRecord(n), n);
}

/** The display rule, with a handle to fall back on before the bare number. */
function nameOr(n, handle) {
  const name = friendName(n);
  return handle && name === formatNumber(n) ? handle : name;
}

/** Who sent a page: the display rule, falling back on the handle it arrived with. */
export function senderName(p) {
  return p?.dir === 'in' ? nameOr(p.peer, p.fromHandle) : friendName(me.value.number);
}

/** Who a page is from / to, for a FROM line: "SAM", or "CREW · SAM" for a group page. */
export function pageFrom(p) {
  if (!p) return '';
  if (!p.groupId) return friendName(p.peer);
  const group = groupLabel(p.groupId, p.groupName);
  return p.dir === 'in' ? `${group} · ${senderName(p)}` : group;
}

/** A sent page's mark: `D 3/5` for a group page, else the usual one. */
export const markFor = (p) => deliveryMark(p);

function putFriend(serverFriend, extra) {
  if (!serverFriend) return null;
  friends.value = upsertFriend(friends.value, serverFriend, extra);
  return friendByNumber(serverFriend.number);
}

export async function addFriendByNumber({ number, name = '', firstPage = '' } = {}) {
  const n = parseNumberInput(number);
  if (!n) return err('bad_number', 'Pager numbers look like 555-382-9104.');
  if (n === me.value.number) return err('self', "That's your own number.");
  if (n === OPERATOR_NUMBER) return err('system_pager', 'The operator is already on your list.');
  const existing = friendByNumber(n);
  if (existing?.status === 'accepted') return err('already_friends', `${displayName(existing)} is already a friend.`);
  if (existing?.status === 'pending-out') return err('already_requested', 'You already sent them a request.');
  const first = normalizeBody(firstPage);
  if (first) {
    const problem = validateBody(first);
    if (problem) return err('bad_body', problem);
  }
  const localName = String(name ?? '').trim().slice(0, 24);

  const clientId = first ? newClientId() : null;
  let friend;
  if (isDemo.value) {
    friend = putFriend({ number: n, handle: null, status: 'pending-out' }, { name: localName || existing?.name || '' });
  } else {
    const r = await api.post('/api/friends', first
      ? { number: n, firstPage: first, firstPageClientId: clientId }
      : { number: n });
    if (!r.ok) return r;
    friend = putFriend(r.friend, { name: localName || existing?.name || '' });
  }
  if (first) {
    const now = Date.now();
    const page = {
      id: clientId, clientId, dir: 'out', peer: n, body: first,
      sentAt: now, receivedAt: now, status: 'network', read: true, locked: false,
    };
    commitPages([page, ...pages.value], [page]);
    counters.sent += 1;
    countCode(first);
    flags.sentOnce = true;
  }
  announce(friend?.status === 'accepted'
    ? `${friendName(n)} added.`
    : `Request sent to ${formatNumber(n)}.`);
  return ok({ friend });
}

export async function acceptInvite(code) {
  const c = String(code ?? '').trim();
  if (!c) return err('invite_not_found', 'That invite link has expired.');
  if (isDemo.value) {
    const friend = putFriend({ number: '5556020144', handle: 'SAM', status: 'accepted' });
    return ok({ friend });
  }
  const a = await activate();
  if (!a.ok) return a;
  const r = await api.post(`/api/friends/invite/${encodeURIComponent(c)}`);
  if (!r.ok) return r;
  const friend = putFriend(r.friend);
  refreshInbox();
  announce(`${friendName(friend.number)} added.`);
  return ok({ friend });
}

/** Whose invite link this is, before pairing: `{ ok, number, handle }`. */
export async function lookupInvite(code) {
  const c = String(code ?? '').trim();
  if (!c) return err('invite_not_found', 'That invite link has expired.');
  if (isDemo.value) return ok({ number: '5556020144', handle: 'SAM' });
  const r = await api.get(`/api/invite/${encodeURIComponent(c)}`, { auth: false });
  if (!r.ok) return r;
  return ok({ number: r.number, handle: r.handle ?? null });
}

async function friendAction(n, path, demoStatus) {
  const number = String(n ?? '');
  if (!friendByNumber(number) && !/^\d{10}$/.test(number)) return err('bad_number', 'Pager numbers look like 555-382-9104.');
  if (isDemo.value) {
    const f = friendByNumber(number);
    if (!f) return err('not_found', 'No pager has that number.');
    const friend = putFriend({ ...f, status: demoStatus });
    return ok({ friend });
  }
  const r = await api.post(`/api/friends/${number}/${path}`);
  if (!r.ok) return r;
  if (!r.friend || !r.friend.status) {
    friends.value = friends.value.filter((f) => f.number !== number);
    return ok({ friend: null });
  }
  return ok({ friend: putFriend(r.friend) });
}

export async function acceptFriend(n) {
  const r = await friendAction(n, 'accept', 'accepted');
  if (r.ok) {
    announce(`${friendName(n)} accepted.`);
    refreshInbox();          // their attached first page is delivered now
  }
  return r;
}

export async function blockFriend(n) {
  if (String(n) === OPERATOR_NUMBER) return err('system_pager', "The operator can't be blocked.");
  const r = await friendAction(n, 'block', 'blocked');
  if (r.ok) announce(`${friendName(n)} blocked.`);
  return r;
}

export async function unblockFriend(n) {
  const r = await friendAction(n, 'unblock', 'accepted');
  if (r.ok) announce(`${formatNumber(n)} unblocked.`);
  return r;
}

export async function removeFriend(n) {
  const number = String(n ?? '');
  if (number === OPERATOR_NUMBER) {
    // Removable from view only; the network keeps the operator.
    friends.value = friends.value.map((f) => (f.number === number ? { ...f, hidden: true } : f));
    return ok();
  }
  if (!isDemo.value) {
    const r = await api.del(`/api/friends/${encodeURIComponent(number)}`);
    if (!r.ok) return r;
  }
  const name = friendName(number);
  friends.value = friends.value.filter((f) => f.number !== number);
  announce(`${name} removed.`);
  return ok();
}

export const cancelRequest = (n) => removeFriend(n);

/**
 * Save a number as a person: your own name for them, only on this phone. ''
 * goes back to the name they chose. A group-mate who isn't a friend can be
 * named too — they get a local record with status `member` (never sent).
 */
export function renameFriend(n, name) {
  const number = String(n ?? '');
  const clean = String(name ?? '').trim().replace(/\s+/g, ' ').slice(0, 24);
  if (!friendByNumber(number)) {
    const m = memberRecord(number);
    if (!m) return err('not_found', 'No friend has that number.');
    friends.value = [...friends.value, {
      number, name: clean, handle: m.handle ?? null, status: 'member', addedAt: Date.now(), lastPageAt: 0, system: false,
    }];
  } else {
    friends.value = friends.value.map((f) => (f.number === number ? { ...f, name: clean } : f));
  }
  announce(clean ? `Saved as ${clean}.` : `Back to ${friendName(number)}.`);
  return ok();
}

export async function rotateInvite() {
  if (isDemo.value) {
    me.value = { ...me.value, inviteCode: Math.random().toString(36).slice(2, 10).toUpperCase() };
    return ok({ inviteCode: me.value.inviteCode });
  }
  const r = await api.post('/api/pager/invite/rotate');
  if (!r.ok) return r;
  me.value = { ...me.value, inviteCode: r.inviteCode };
  announce('New invite link made. The old one no longer works.');
  return ok({ inviteCode: r.inviteCode });
}

// ── actions: settings ────────────────────────────────────────────────────────

export async function setHandle(h) {
  const clean = String(h ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
  if (clean && !HANDLE_RE.test(clean)) return err('bad_handle', 'Handles are up to 12 letters and numbers.');
  if (clean.replace(/[^A-Z0-9]/g, '').includes('OPERATOR')) return err('bad_handle', 'That handle is taken by the network.');
  const previous = me.value.handle;
  me.value = { ...me.value, handle: clean || null };
  flags.nameAsked = true;
  if (isDemo.value) return ok();
  const r = await api.put('/api/pager/prefs', { handle: clean || null });
  if (!r.ok && !isTransient(r)) {
    me.value = { ...me.value, handle: previous };
    return r;
  }
  if (!r.ok) queuePrefs({ handle: clean || null });
  return ok();
}

export function setDevice(id) {
  if (!DEVICES.some((d) => d.id === id)) return err('bad_device', "That isn't a pager model.");
  settings.device = id;
  queuePrefs({ device: id });
  return ok();
}

export function setAlertMode(m) {
  if (!ALERT_MODES.includes(m)) return err('bad_alert_mode', 'Alert mode is beep, vibrate or silent.');
  settings.alertMode = m;
  queuePrefs({ alertMode: m });
  return ok();
}

export function setSetting(key, value) {
  switch (key) {
    case 'device': return setDevice(value);
    case 'alertMode': return setAlertMode(value);
    case 'highContrast': settings.highContrast = !!value; return ok();
    case 'largeControls': settings.largeControls = !!value; return ok();
    case 'textSize': {
      const v = Number(value);
      if (!TEXT_SIZES.includes(v)) return err('bad_text_size', 'Text size is 1, 1.25, 1.5 or 2.');
      settings.textSize = v;
      return ok();
    }
    default: return err('bad_setting', 'Unknown setting.');
  }
}

// ── actions: presets ─────────────────────────────────────────────────────────

export function addPreset(text) {
  const clean = normalizeBody(text);
  const problem = validateBody(clean);
  if (problem) return err('bad_body', problem);
  const preset = { id: presetId(), text: clean };
  presets.value = [...presets.value, preset];
  return ok({ preset });
}

export function updatePreset(id, text) {
  const clean = normalizeBody(text);
  const problem = validateBody(clean);
  if (problem) return err('bad_body', problem);
  if (!presets.value.some((p) => p.id === id)) return err('not_found', 'That preset is gone.');
  presets.value = presets.value.map((p) => (p.id === id ? { ...p, text: clean } : p));
  return ok();
}

export function removePreset(id) {
  const at = presets.value.findIndex((p) => p.id === id);
  if (at < 0) return err('not_found', 'That preset is gone.');
  const preset = presets.value[at];
  presets.value = presets.value.filter((p) => p.id !== id);
  removeWithUndo({
    text: 'Preset deleted.',
    revert: () => {
      const list = [...presets.value];
      list.splice(Math.min(at, list.length), 0, preset);
      presets.value = list;
    },
  });
  return ok();
}

export function movePreset(from, to) {
  presets.value = moveItem(presets.value, from, to);
  return ok();
}

// ── actions: report, export ──────────────────────────────────────────────────

export async function reportPage(page, reason) {
  const number = String(page?.peer ?? page?.number ?? '');
  if (!/^\d{10}$/.test(number)) return err('bad_number', 'Pager numbers look like 555-382-9104.');
  const why = String(reason ?? '').trim();
  if (!why) return err('bad_reason', 'Pick a reason.');
  if (isDemo.value) {
    if (number !== OPERATOR_NUMBER) await friendAction(number, 'block', 'blocked');
    return ok();
  }
  const r = await api.post('/api/reports', {
    number,
    body: page?.body ?? page?.firstPage ?? '',
    sentAt: page?.sentAt ?? page?.since ?? null,
    reason: why,
  });
  if (!r.ok) return r;
  if (number !== OPERATOR_NUMBER) {
    const f = friendByNumber(number);
    if (f) friends.value = friends.value.map((x) => (x.number === number ? { ...x, status: 'blocked' } : x));
    else putFriend({ number, handle: null, status: 'blocked' });
  }
  announce('Reported and blocked.');
  return ok();
}

export function exportHistory() {
  const header = `PAGE ME · ${formatNumber(me.value.number ?? '')} · exported ${formatIso(Date.now())}`;
  const body = exportLines(pages.value, (peer, p) => (p?.groupId ? pageFrom(p) : friendName(peer)), formatIso, (s) => STATUS_MARK[s] ?? '?');
  return `${header}\n\n${body}\n`;
}

// ── actions: beeps + activation card ─────────────────────────────────────────

export async function enableBeeps() {
  if (isDemo.value || !pushAvailable()) {
    flags.notifAsked = 2;
    return 'unavailable';
  }
  const outcome = await requestBeeps();
  flags.notifAsked = 2;
  return outcome;
}

export function dismissBeepsOffer() {
  if (flags.notifAsked === 0) flags.notifAsked = 1;
  else if (flags.notifAsked === 1 && flags.reoffer) flags.reoffered = true;
}

export function markActivationCardShown() {
  me.value = { ...me.value, activationCodeShown: true };
}

/** "What should friends see?" — Skip. Settings and Friends still have the field. */
export function dismissNameOffer() {
  flags.nameAsked = true;
}

// ── actions: groups ──────────────────────────────────────────────────────────
//
// A group page is one page to a named group of up to 12 pagers. You add your
// own accepted friends; after that everyone in it gets everyone's group pages.

export function groupById(id) {
  return groups.value.find((g) => g.id === String(id ?? ''));
}

/** A group's name, or the name its pages carried (after you left), or GROUP. */
export function groupLabel(id, fallback) {
  return groupById(id)?.name || fallback || 'GROUP';
}

/** A group's thread, oldest first: pages, with "JESS JOINED" lines between them (`system: true, text`). */
export function groupThread(id) {
  const g = groupById(id);
  // A pager you blocked is silent here too.
  const shown = pages.value.filter((p) => !(p.dir === 'in' && blockedSet.value.has(p.peer)));
  if (!g) return shown.filter((p) => p.groupId === String(id)).sort((a, b) => a.sentAt - b.sentAt);
  const nameOf = (n, handle) => (n === me.value.number ? 'YOU' : nameOr(n, handle));
  return groupTimeline(shown, g).map((x) => (x.system ? { ...x, text: eventLine(x.event, nameOf) } : x));
}

/** Friends you could add to a group (accepted, not the operator, not already in it). */
export function addableFriends(id) {
  const inIt = new Set(groupById(id)?.members.map((m) => m.number) ?? []);
  return acceptedFriends.value.filter((f) => !f.system && !inIt.has(f.number));
}

export function cleanGroupName(name) {
  return String(name ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
}

function groupNameProblem(clean) {
  if (!clean) return 'Give the group a name.';
  if (!GROUP_NAME_RE.test(clean)) return 'Group names are up to 16 letters and numbers.';
  return null;
}

function putGroup(serverGroup) {
  if (!serverGroup) return null;
  const [g] = mergeGroups(groups.value, [serverGroup]);
  const at = groups.value.findIndex((x) => x.id === g.id);
  const next = [...groups.value];
  if (at >= 0) next[at] = g; else next.unshift(g);
  groups.value = next;
  return g;
}

function dropGroup(id) {
  groups.value = groups.value.filter((g) => g.id !== id);
}

const memberOf = (n) => ({ number: n, handle: friendByNumber(n)?.handle ?? memberRecord(n)?.handle ?? null });

export async function createGroup(name, numbers = []) {
  const clean = cleanGroupName(name);
  const problem = groupNameProblem(clean);
  if (problem) return err('bad_group_name', problem);
  const list = [...new Set((numbers ?? []).map(String))]
    .filter((n) => n !== me.value.number && n !== OPERATOR_NUMBER && friendByNumber(n)?.status === 'accepted');
  if (!list.length) return err('no_members', 'Pick at least one friend.');
  if (list.length + 1 > MAX_GROUP) return err('group_full', `Groups are ${MAX_GROUP} pagers at most.`);
  let group;
  if (isDemo.value) {
    const at = Date.now();
    group = putGroup({
      id: `demo-g-${at.toString(36)}`, name: clean, owner: me.value.number,
      members: [me.value.number, ...list].map(memberOf),
      events: [{ at, kind: 'created', by: me.value.number, name: clean }],
      createdAt: at, updatedAt: at, lastPageAt: null,
    });
  } else {
    const r = await api.post('/api/groups', { name: clean, members: list });
    if (!r.ok) return r;
    group = putGroup(r.group);
  }
  announce(`${clean} made, with ${list.length + 1} pagers.`);
  return ok({ group });
}

export async function addToGroup(id, numbers = []) {
  const g = groupById(id);
  if (!g) return err('group_not_found', "That group is gone, or you're not in it.");
  const list = [...new Set((numbers ?? []).map(String))].filter((n) => !g.members.some((m) => m.number === n));
  if (!list.length) return ok({ group: g });
  if (g.members.length + list.length > MAX_GROUP) return err('group_full', `Groups are ${MAX_GROUP} pagers at most.`);
  let group;
  if (isDemo.value) {
    const at = Date.now();
    group = putGroup({
      ...g,
      members: [...g.members, ...list.map(memberOf)],
      events: [...g.events, ...list.map((n) => ({ at, kind: 'joined', who: n, by: me.value.number }))],
      updatedAt: at,
    });
  } else {
    const r = await api.post(`/api/groups/${encodeURIComponent(g.id)}/members`, { numbers: list });
    if (!r.ok) return r;
    group = putGroup(r.group);
  }
  announce(list.length === 1 ? `${friendName(list[0])} added to ${g.name}.` : `${list.length} added to ${g.name}.`);
  return ok({ group });
}

/** Leave a group. `block: true` also stops anyone adding you back. */
export async function leaveGroup(id, { block = false } = {}) {
  const g = groupById(id);
  if (!g) return ok();
  if (!isDemo.value) {
    const r = await api.del(`/api/groups/${encodeURIComponent(g.id)}/members/${me.value.number}`, undefined, block ? { block: true } : undefined);
    if (!r.ok && r.error !== 'group_not_found') return r;
  }
  dropGroup(g.id);
  announce(`You left ${g.name}.`);
  return ok();
}

/** The owner takes someone out of a group. */
export async function removeFromGroup(id, number) {
  const g = groupById(id);
  const n = String(number ?? '');
  if (!g) return err('group_not_found', "That group is gone, or you're not in it.");
  if (g.owner !== me.value.number) return err('not_owner', "Only the group's owner can remove people.");
  const who = friendName(n);
  if (isDemo.value) {
    const at = Date.now();
    putGroup({
      ...g,
      members: g.members.filter((m) => m.number !== n),
      events: [...g.events, { at, kind: 'removed', who: n, by: me.value.number }],
      updatedAt: at,
    });
  } else {
    const r = await api.del(`/api/groups/${encodeURIComponent(g.id)}/members/${encodeURIComponent(n)}`);
    if (!r.ok) return r;
    if (r.group) putGroup(r.group); else dropGroup(g.id);
  }
  announce(`${who} removed from ${g.name}.`);
  return ok();
}

export async function renameGroup(id, name) {
  const g = groupById(id);
  if (!g) return err('group_not_found', "That group is gone, or you're not in it.");
  if (g.owner !== me.value.number) return err('not_owner', "Only the group's owner can rename it.");
  const clean = cleanGroupName(name);
  const problem = groupNameProblem(clean);
  if (problem) return err('bad_group_name', problem);
  if (clean === g.name) return ok({ group: g });
  let group;
  if (isDemo.value) {
    const at = Date.now();
    group = putGroup({ ...g, name: clean, events: [...g.events, { at, kind: 'renamed', by: me.value.number, name: clean }], updatedAt: at });
  } else {
    const r = await api.patch(`/api/groups/${encodeURIComponent(g.id)}`, { name: clean });
    if (!r.ok) return r;
    group = putGroup(r.group);
  }
  announce(`Renamed ${clean}.`);
  return ok({ group });
}

/** Page everyone in a group at once. Same outbox, same retry, one clientId. */
export async function sendGroupPage(id, body) {
  const g = groupById(id);
  if (!g) return err('group_not_found', "That group is gone, or you're not in it.");
  const problem = validateBody(body);
  if (problem) return err('bad_body', problem);
  await ready;
  const now = Date.now();
  const clientId = newClientId();
  const page = {
    id: clientId, clientId, dir: 'out', peer: '', groupId: g.id, groupName: g.name, body: normalizeBody(body),
    sentAt: now, receivedAt: now, status: 'queued', read: true, locked: false,
    recipients: Math.max(0, g.members.length - 1), deliveredBy: [],
  };
  commitPages([page, ...pages.value], [page]);
  counters.sent += 1;
  countCode(page.body);
  groups.value = groups.value.map((x) => (x.id === g.id ? { ...x, lastPageAt: now } : x));
  delete drafts[groupDraftKey(g.id)];
  flags.sentOnce = true;
  haptic('tick');

  if (isDemo.value) {
    demoDeliver(page);
    return ok({ page });
  }
  if (!me.value.token) return ok({ page, queued: true });
  const r = await postPage(page);
  const current = pages.value.find((p) => p.id === page.id) ?? page;
  if (r.ok) {
    for (const ms of [3000, 8000]) setTimeout(() => { refreshInbox(); }, ms);
    return ok({ page: current });
  }
  if (current.status === 'queued') {
    scheduleRetry();
    return ok({ page: current, queued: true });
  }
  return err(r.error, r.message, { page: current });
}

/** Where a group's unsent draft is kept in `drafts`. */
export const groupDraftKey = (id) => `g:${id}`;

// ── demo ─────────────────────────────────────────────────────────────────────

const DEMO_ME = {
  number: '5553829104', handle: 'ALEX', inviteCode: 'DEMO7Q2K',
  activationCode: 'K7QX-9MFD-2HTP', activationCodeShown: true, token: null,
};
const DEMO_FRIENDS = {
  MUM: '5552140987', DAZ: '5557731260', JESS: '5554418835', REQ: '5552017788',
};
const DEMO_GROUP = 'demo-crew';

function buildDemo() {
  const now = Date.now();
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  const f = (number, handle, status, extra = {}) => ({
    number, name: '', handle, status, addedAt: now - 20 * day, lastPageAt: 0, system: false, ...extra,
  });
  const friendsSeed = [
    f(DEMO_FRIENDS.MUM, 'MUM', 'accepted'),
    f(DEMO_FRIENDS.DAZ, 'DAZ', 'accepted'),
    f(DEMO_FRIENDS.JESS, 'JESS', 'accepted'),
    f(DEMO_FRIENDS.REQ, 'KEV', 'pending-in', { firstPage: 'HEY ITS KEV FROM WORK 👋', addedAt: now - 3 * hr }),
  ];
  let n = 0;
  const p = (dir, peer, body, ago, extra = {}) => {
    n += 1;
    return {
      id: `demo-${n}`, clientId: `demo-${n}`, dir, peer, body,
      sentAt: now - ago, receivedAt: now - ago,
      status: dir === 'in' ? 'delivered' : 'delivered', read: true, locked: false, ...extra,
    };
  };
  const pagesSeed = [
    p('in', OPERATOR_NUMBER, 'WELCOME. PAGE 411 FOR HELP ☎️', 3 * day),
    p('out', OPERATOR_NUMBER, '143', 3 * day - 2 * min),
    p('in', OPERATOR_NUMBER, '143 TO YOU TOO ❤️', 3 * day - 2 * min + 3000),
    p('in', DEMO_FRIENDS.MUM, '143', 26 * hr, { locked: true }),
    p('in', DEMO_FRIENDS.DAZ, 'PIZZA? 🍕', 5 * hr),
    p('out', DEMO_FRIENDS.DAZ, 'ON MY WAY 🚗', 5 * hr - 4 * min),
    p('in', DEMO_FRIENDS.JESS, '07734 👋', 2 * hr),
    p('out', DEMO_FRIENDS.JESS, 'CALL ME', 90 * min, { status: 'network' }),
    p('in', DEMO_FRIENDS.DAZ, '911', 35 * min, { read: false }),
    p('in', DEMO_FRIENDS.JESS, 'RUNNING LATE 🏃', 12 * min, { read: false }),
    p('out', DEMO_FRIENDS.MUM, '143 ❤️', 4 * min, { status: 'queued' }),
  ];
  // One group page conversation: ALEX, DAZ and JESS as CREW.
  const g = (dir, peer, body, ago, extra = {}) => p(dir, peer, body, ago, { groupId: DEMO_GROUP, groupName: 'CREW', ...extra });
  pagesSeed.push(
    g('in', DEMO_FRIENDS.DAZ, "WHO'S UP FOR KARAOKE 🎤", 4 * hr),
    g('in', DEMO_FRIENDS.JESS, '143 😂', 4 * hr - 6 * min),
    g('out', '', 'COUNT ME IN', 3 * hr, { recipients: 2, deliveredBy: [DEMO_FRIENDS.DAZ, DEMO_FRIENDS.JESS] }),
    g('in', DEMO_FRIENDS.DAZ, '10-4. 8PM AT THE BAR 🎶', 20 * min, { read: false }),
  );
  const groupsSeed = [{
    id: DEMO_GROUP,
    name: 'CREW',
    owner: DEMO_ME.number,
    members: [
      { number: DEMO_ME.number, handle: DEMO_ME.handle },
      { number: DEMO_FRIENDS.DAZ, handle: 'DAZ' },
      { number: DEMO_FRIENDS.JESS, handle: 'JESS' },
    ],
    events: [{ at: now - 2 * day, kind: 'created', by: DEMO_ME.number, name: 'CREW' }],
    createdAt: now - 2 * day,
    updatedAt: now - 2 * day,
    lastPageAt: now - 20 * min,
  }];
  for (const fr of friendsSeed) {
    const last = pagesSeed.filter((x) => x.peer === fr.number && !x.groupId).reduce((m, x) => Math.max(m, x.sentAt), 0);
    fr.lastPageAt = last;
  }
  return { friendsSeed, pagesSeed, groupsSeed };
}

// demoData() honours `appfactory:qa:seed === 'empty'` (→ [] for both).
const demoSeed = demoData([buildDemo()]);

function seedDemo() {
  const seed = demoSeed.value[0];
  me.value = { ...DEMO_ME };
  const op = { ...operatorFriend(Date.now() - 30 * 86_400_000) };
  friends.value = [op, ...(seed?.friendsSeed ?? [])];
  groups.value = seed?.groupsSeed ?? [];
  pages.value = sortPages(seed?.pagesSeed ?? []);
  op.lastPageAt = pages.value.find((x) => x.peer === OPERATOR_NUMBER)?.sentAt ?? 0;
  presets.value = defaultPresets();
  activation.value = 'ready';
}

const DEMO_OPERATOR_LINES = [
  'OPERATOR HERE. LOUD AND CLEAR 📟',
  'COPY THAT. 10-4 📟',
  'GOT YOUR PAGE. NOW PAGE A FRIEND 👋',
  'BEEP BEEP. THAT WAS ME. HI 👋',
];
const DEMO_DAZ_LINES = ['HA 😂', '10-4', 'OK 👍', 'SEE YOU THERE 🍕'];
let demoReplyN = 0;

function demoReplyFor(peer, body) {
  const c = codeFor(body)?.code;
  if (peer === OPERATOR_NUMBER) {
    if (c === '411') return 'ADD FRIENDS BY # OR INVITE LINK. PAGE UP TO 160 CHARS. TRY 143 = I LOVE YOU. OPERATOR ALWAYS ON 📟';
    if (['143', '1432', '459', '831'].includes(c)) return '143 TO YOU TOO ❤️';
    if (c === '911') return 'THIS IS A TOY PAGER. IN AN EMERGENCY CALL YOUR LOCAL EMERGENCY NUMBER.';
    demoReplyN += 1;
    return DEMO_OPERATOR_LINES[demoReplyN % DEMO_OPERATOR_LINES.length];
  }
  if (peer === DEMO_FRIENDS.DAZ) {
    if (c === '143') return '143 🙄';
    demoReplyN += 1;
    return DEMO_DAZ_LINES[demoReplyN % DEMO_DAZ_LINES.length];
  }
  return null;
}

function demoDeliver(page) {
  setTimeout(() => { if (pages.value.find((p) => p.id === page.id)?.status === 'queued') patchPage(page.id, { status: 'network' }); }, 600);
  const everyone = page.groupId
    ? (groupById(page.groupId)?.members ?? []).map((m) => m.number).filter((n) => n !== me.value.number)
    : [];
  setTimeout(() => {
    if (!pages.value.some((p) => p.id === page.id)) return;
    patchPage(page.id, page.groupId ? { status: 'delivered', deliveredBy: everyone } : { status: 'delivered' });
  }, 1800);
  // In a group, DAZ answers for everyone (if he is still in it).
  const from = page.groupId ? everyone.find((n) => n === DEMO_FRIENDS.DAZ) : page.peer;
  const reply = from ? demoReplyFor(from, page.body) : null;
  if (!reply) return;
  setTimeout(() => {
    const at = Date.now();
    const incoming = {
      id: `demo-r-${at}`, clientId: `demo-r-${at}`, dir: 'in', peer: from, body: reply,
      sentAt: at, receivedAt: at, status: 'delivered', read: false, locked: false,
      ...(page.groupId ? { groupId: page.groupId, groupName: page.groupName } : {}),
    };
    commitPages([incoming, ...pages.value]);
    arrived([incoming]);
  }, 3000);
}

// ── wiring ───────────────────────────────────────────────────────────────────

setAlertSource(() => ({ mode: settings.alertMode, beep: currentDevice.value.beep }));

hydrate();

watch(() => [settings.textSize, settings.highContrast, settings.largeControls], applyDisplay, { immediate: true });
watch([me, friends, groups, presets, () => ({ ...settings }), () => ({ ...flags }), () => ({ ...counters, codes: { ...counters.codes } })],
  savePrefsNow, { deep: true });
watch(drafts, saveDrafts, { deep: true });

/** Test/debug seam: resolves once stored pages are loaded. */
export const whenReady = () => ready;
